import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link } from 'react-router-dom';

import { useGetBeneficiaryVirtualAccountsQuery, useGetOwnIntegratorFeaturesQuery } from '@/api/v1';
import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { BalanceWidget } from '@/components/molecules/BalanceWidget';
import { FF_DASHBOARD_ALLOW_VA_CREATION_FOR_INDIVIDUALS } from '@/data/featureFlags';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { useTranslator } from '@/hooks/useTranslator';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { BeneficiaryType } from '@/types/customer';
import { Permission } from '@/types/permissions';

interface Props {
  readonly balances?: readonly DetailedBalance[];
  readonly beneficiaryId?: string;
  readonly beneficiaryType?: BeneficiaryType;
  readonly onCreateUsdAccountClick?: () => void;
}

export const BalanceLayout: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    balances = [DetailedBalance.zero(CurrencyCodeType.USDC)],
    beneficiaryId,
    beneficiaryType = BeneficiaryType.business,
    onCreateUsdAccountClick,
  } = props;
  const { userIsAuthorizedTo } = usePermissionsManager();
  const tr = useTranslator();

  const { data: virtualAccounts = [] } = useGetBeneficiaryVirtualAccountsQuery(
    beneficiaryId ?? '',
    { skip: !beneficiaryId },
  );
  const hasUsdVirtualAccount = virtualAccounts.length > 0;
  const { data: integratorFeatures, isLoading: isLoadingIntegratorFeatures } =
    useGetOwnIntegratorFeaturesQuery();
  const isVaEnabled = integratorFeatures?.vaEnabled ?? false;
  const allowVaCreationForIndividuals = useIntegratorFeatureFlag(
    FF_DASHBOARD_ALLOW_VA_CREATION_FOR_INDIVIDUALS,
  );
  const canCreateUsdVirtualAccount =
    beneficiaryType === BeneficiaryType.business || allowVaCreationForIndividuals;

  const canViewUsdAccount = userIsAuthorizedTo(Permission.VIEW_USD_ACCOUNT);
  const balanceList = balances.filter(
    (balance: DetailedBalance): boolean =>
      balance.currencyCode !== CurrencyCodeType.USD || canViewUsdAccount,
  );

  return (
    <Box display="flex" flexDirection="column" px={4} py={5} gap={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h7">{tr('ACCOUNT')}</Typography>
        {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
          <ButtonBase component={Link} to="convert" sx={styles.button}>
            <Typography fontSize={16} fontWeight={400} lineHeight="24px">
              {tr('CONVERT')}
            </Typography>
          </ButtonBase>
        )}
      </Box>
      {balanceList.map((balance: DetailedBalance): React.ReactElement => {
        const isUsdWithoutAccount =
          balance.currencyCode === CurrencyCodeType.USD && !hasUsdVirtualAccount;

        return (
          <Box key={balance.currencyCode} sx={styles.balanceRow}>
            <Box sx={styles.balanceWidgetWrapper}>
              <BalanceWidget
                balance={balance}
                showDetailsButton={!isUsdWithoutAccount}
                beneficiaryId={isUsdWithoutAccount ? undefined : beneficiaryId}
              />
            </Box>
            {isUsdWithoutAccount &&
              canCreateUsdVirtualAccount &&
              userIsAuthorizedTo(Permission.CREATE_USD_ACCOUNT) && (
                <Box sx={styles.createButtonWrapper}>
                  <Tooltip
                    title={!isVaEnabled ? tr('CREATE_USD_ACCOUNT_DISABLED_TOOLTIP') : ''}
                    placement="top">
                    <Box component="span">
                      <Button
                        variant="contained"
                        size="compact"
                        disabled={!isVaEnabled}
                        endIcon={
                          !isVaEnabled ? (
                            <InfoIcon fill="currentColor" width={16} height={16} />
                          ) : undefined
                        }
                        loading={isLoadingIntegratorFeatures}
                        loadingPosition="start"
                        onClick={onCreateUsdAccountClick}>
                        {tr('CREATE_USD_ACCOUNT')}
                      </Button>
                    </Box>
                  </Tooltip>
                </Box>
              )}
          </Box>
        );
      })}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  button: {
    height: 40,
    width: 110,
    lineHeight: '40px',
    backgroundColor: 'custom.InputsBackground',
    borderRadius: 5,
  },
  balanceRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  balanceWidgetWrapper: {
    flex: 1,
  },
  createButtonWrapper: {
    position: 'absolute',
    right: 24,
  },
};
